import { toast } from 'react-toastify'
import { mainClient, shareClient } from './MeetingClient'
import api from './api'

class MeetingCoordinator {
    constructor ( stores ) {
        this.stores = stores
        this.recorderInterval = null
    }

    async syncMediaPermissions ( localUser ) {
        const { MeetingStore, UserStore } = this.stores
        const { isVideoOn, isAudioOn } = MeetingStore.getMeetingState
        const { enable_video_feed, enable_microphone } = MeetingStore.getCurrentMeetingSettings

        const isHost = ["ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role ) ||
            localUser.meeting_role === "meeting-host"

        // Handle Video Auto-Publish
        if ( isVideoOn ) {
            if ( enable_video_feed || isHost ) {
                await mainClient.publish( 'video' )
            } else {
                await mainClient.unpublish( 'video' )
                MeetingStore.setMeetingState( { isVideoOn: false } )
            }
        }

        // Handle Audio Auto-Publish
        if ( isAudioOn ) {
            if ( enable_microphone || isHost ) {
                await mainClient.publish( 'audio' )
            } else {
                await mainClient.unpublish( 'audio' )
                MeetingStore.setMeetingState( { isAudioOn: false } )
                toast( "Microphone disabled by room settings." )
            }
        }
    }

    async toggleCamera ( forceState ) {
        const { MeetingStore } = this.stores

        let { isVideoOn, isJoined, selectedCamera, isVideoPublished } = MeetingStore.getMeetingState

        const targetState = typeof forceState === 'boolean' ? forceState : !isVideoOn

        try {
            if ( targetState ) {

                if ( mainClient.videoTrack ) return


                const track = await mainClient.createCameraTrack( selectedCamera )

                MeetingStore.setLocalVideoTrack( track )

                if ( !isJoined ) {
                    track.play( `camera-preview` )
                }

                MeetingStore.setMeetingState( { isVideoOn: true, isCameraDisabled: false, isStartingVideo: true } )
                MeetingStore.setLocalUserDetails( { isVideoOn: true } )
                return true
            } else {
                // If publishing, unpublish first
                if ( isVideoPublished ) {
                    await mainClient.unpublish( 'video' )
                    MeetingStore.setMeetingState( { isVideoPublished: false } )
                }
                // ONLY stop video, leave audio alone
                mainClient.stopVideoTrack()
                MeetingStore.setLocalVideoTrack( null )
                MeetingStore.setMeetingState( { isVideoOn: false } )
                MeetingStore.setLocalUserDetails( { isVideoOn: false } )
                return true
            }
        } catch ( err ) {
            toast.error( "Camera access failed." )
            return false
        }
    }

    async toggleMicrophone ( forceState ) {
        const { MeetingStore } = this.stores
        const targetState = typeof forceState === 'boolean' ? forceState : !MeetingStore.getMeetingState.isAudioOn

        try {
            if ( targetState ) {

                if ( mainClient.audioTrack ) return

                const track = await mainClient.createMicrophoneTrack( MeetingStore.getMeetingState.selectedMic )

                MeetingStore.setLocalAudioTrack( track )
                MeetingStore.setMeetingState( { isAudioOn: true, isMicDisabled: track?.getMediaStreamTrack()?.muted } )
                MeetingStore.setLocalUserDetails( { isAudioOn: true } )

                return true
            } else {
                // If publishing, unpublish first
                if ( MeetingStore.getMeetingState.isAudioPublished ) {
                    await mainClient.unpublish( 'audio' )
                    MeetingStore.setMeetingState( { isAudioPublished: false } )
                }
                // ONLY stop audio, leave video alone
                mainClient.stopAudioTrack()
                MeetingStore.setLocalAudioTrack( null )
                MeetingStore.setMeetingState( { isAudioOn: false } )
                MeetingStore.setLocalUserDetails( { isAudioOn: false } )
                return true
            }
        } catch ( err ) {
            toast.error( "Microphone access failed." )
            return false
        }
    }

    async publishVideo () {
        const { MeetingStore } = this.stores
        try {
            if ( MeetingStore.getMeetingState.isVideoPublished ) {
                // Currently published -> Turn off hardware and unpublish
                const success = await this.toggleCamera( false )
                if ( success ) {
                    // mainClient.unpublish is called inside toggleCamera(false) for safety
                    MeetingStore.setMeetingState( { isVideoPublished: false } )
                }
            } else {
                // Currently unpublished -> Turn on hardware then publish
                const success = await this.toggleCamera( true )
                if ( success && mainClient.videoTrack ) {
                    await mainClient.publish( 'video' )
                    MeetingStore.setMeetingState( { isVideoPublished: true } )
                }
            }
        } catch ( err ) {
            console.error( "Publish Video Error:", err )
        }
    }

    async publishAudio () {
        const { MeetingStore } = this.stores
        try {
            if ( MeetingStore.getMeetingState.isAudioPublished ) {
                const success = await this.toggleMicrophone( false )
                if ( success ) {
                    MeetingStore.setMeetingState( { isAudioPublished: false } )
                }
            } else {
                const success = await this.toggleMicrophone( true )
                if ( success && mainClient.audioTrack ) {
                    await mainClient.publish( 'audio' )
                    MeetingStore.setMeetingState( { isAudioPublished: true } )
                }
            }
        } catch ( err ) {
            console.error( "Publish Audio Error:", err )
        }
    }

    async toggleScreenShareAudio ( value ) {
        const { MeetingStore } = this.stores
        try {
            if ( shareClient.audioTrack ) {
                await shareClient.audioTrack.setEnabled( value )
                const { sharingState } = MeetingStore.getMeetingState
                MeetingStore.setMeetingState( {
                    sharingState: { ...sharingState, audioEnabled: value }
                } )
            }
        } catch ( err ) {
            console.error( "Toggle Screen Audio Failed:", err )
        }
    }

    async replaceSharingTrack ( stopScreenShareCallback ) {
        const { MeetingStore } = this.stores
        try {

            const oldVideoTrack = shareClient.videoTrack
            const oldAudioTrack = shareClient.audioTrack
            if ( oldAudioTrack )
                await shareClient.unpublish( 'audio' )

            // 1. Create new tracks
            const newTracks = await shareClient.createScreenTracks( null, stopScreenShareCallback )
            const newVideoTrack = newTracks.video
            const newAudioTrack = newTracks.audio

            // 2. Optimized track replacement for video (avoids flickering)
            if ( oldVideoTrack ) {
                oldVideoTrack?.stop()
                oldVideoTrack?.close()
                await oldVideoTrack.replaceTrack( newVideoTrack.getMediaStreamTrack() )
            }

            // 3. Audio swap (Unpublish old, publish new)
            if ( oldAudioTrack && shareClient.client.connectionState === "CONNECTED" ) {
                await shareClient.unpublish( 'audio' )
                oldAudioTrack.stop()
                oldAudioTrack.close()
            }

            if ( newAudioTrack ) {
                await shareClient.publish( 'audio' )
            }

            // 4. Update Store
            const surface = shareClient.getSharingSurface()
            const { sharingState } = MeetingStore.getMeetingState

            MeetingStore.setMeetingState( {
                isSharing: true,
                sharingState: {
                    surface,
                    screen: true,
                    audio: !!newAudioTrack,
                    audioEnabled: true
                }
            } )


        } catch ( err ) {
            console.error( "Replace Sharing Track Failed:", err )
            toast.error( "Failed to change sharing source." )
        }
    }

    async toggleRecording () {
        const { MeetingStore, OnlineSessionSocketStore } = this.stores
        const isRecording = MeetingStore.getMeetingState.isRecording
        MeetingStore.setMeetingState( { startingOrStopingRecording: true } )
        if ( !isRecording ) {
            try {
                const { data: { data: recording } } = await api.recording.createRecording( this.meetingId )
                const { data: { data: startData } } = await api.recording.startRecording( this.meetingId, recording.recording_id, { url: `${window.location.href}` } )

                const { success, message } = await OnlineSessionSocketStore.emit( `recording:start`, { room_id: this.meetingId, ...startData, recordingId: recording.recording_id } )

                if ( success ) {
                    this.recorderIntervalRef = setInterval( () => {
                        OnlineSessionSocketStore.emit( "recording:heartbeat", { room_id: this.meetingId } )
                    }, 10000 )
                } else if ( message ) {
                    toast( message )
                }
                MeetingStore.setMeetingState( { startingOrStopingRecording: false, isRecording: true, recording_id: recording.recording_id, started_recording_at: new Date() } )
            } catch ( err ) {
                console.log( err )
                MeetingStore.setMeetingState( { startingOrStopingRecording: false } )
                toast( err?.response?.data?.message || "Something went wrong while starting recording." )
            }
        } else {
            try {
                if ( this.recorderIntervalRef )
                    clearInterval( this.recorderIntervalRef )
                this.recorderIntervalRef = null
                await api.recording.stopRecording( this.meetingId, MeetingStore.getMeetingState.recording_id )
                MeetingStore.setMeetingState( { startingOrStopingRecording: false, isRecording: false, recording_id: null, started_recording_at: null } )
            } catch ( err ) {
                console.log( err )
                if ( err?.response?.status === 404 ) {
                    MeetingStore.setMeetingState( { startingOrStopingRecording: false, isRecording: false, recording_id: null, started_recording_at: null } )
                } else
                    toast( err?.response?.data?.message || "Something went wrong while stoping recording." )
            }
        }
    }

    async orchestrateJoin ( meetingId, isRejoining = false, noReturn ) {

        // Tracking connection status for the failsafe
        let socketJoined = false
        let agoraJoined = false
        const { MeetingStore, OnlineSessionSocketStore, VirtualboardStore, UserStore } = this.stores
        try {

            await OnlineSessionSocketStore.connect( meetingId )

            // 1. Socket Join
            const joinResponse = await OnlineSessionSocketStore.emit( 'meeting:join', {
                room_id: meetingId,
                user_id: MeetingStore.getLocalUser.user_id,
                meeting: MeetingStore.getCurrentMeeting,
                rejoining: isRejoining,
            } )

            console.log( {
                room_id: meetingId,
                user_id: MeetingStore.getLocalUser.user_id,
                meeting: MeetingStore.getCurrentMeeting,
                rejoining: isRejoining,
            } )


            if ( !joinResponse.success ) {
                console.log( joinResponse )

                if ( joinResponse.waiting )
                    return MeetingStore.setMeetingState( { waitingToJoin: true } )
                if ( joinResponse.duplicateSession ) return
                throw new Error( joinResponse.message || "Failed to join socket" )
            }

            // 2. Agora Join
            const access = JSON.parse( sessionStorage.getItem( 'meetingAccessToken' ) || '{}' )
            await mainClient.join( meetingId, access.agoraChannelToken, MeetingStore.getLocalUser.user_id, MeetingStore.getLocalUser )

            agoraJoined = true

            const { currentUsers, pendingRequests, handRaises, userPermissions, virtualBoardActive, localUser } = joinResponse
            const isPrivileged = localUser.meeting_role === "meeting-host" || ["ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role )

            const { isVideoOn, isAudioOn } = MeetingStore.getMeetingState
            const { enable_video_feed, enable_microphone } = MeetingStore.getCurrentMeetingSettings

            if ( isVideoOn ) {
                if ( enable_video_feed && isPrivileged ) {
                    this.toggleCamera( true )
                    await mainClient.publish( "video" )
                }
                else if ( !enable_video_feed && localUser.meeting_role !== "meeting-host" ) {
                    this.toggleCamera( false )
                }
            }

            if ( isAudioOn ) {
                if ( enable_microphone && isPrivileged ) {
                    this.toggleMicrophone( true )
                    await mainClient.publish( "audio" )
                } else if ( !enable_microphone && localUser.meeting_role !== "meeting-host" ) {
                    await this.toggleMicrophone( false )
                    toast( `Switching mic off.` )
                }
            }

            await mainClient.publish()

            // 3. Sync State

            MeetingStore.setLocalUserDetails( { ...localUser, type: "local-client" } )
            MeetingStore.setMeetingState( { isJoined: true, isVideoPublished: MeetingStore.getMeetingState.isVideoOn, isAudioPublished: MeetingStore.getMeetingState.isAudioOn, waitingToJoin: false } )
            MeetingStore.addOrUpdateRemoteUsers( currentUsers, 'alive-socket-server', "added", false )
            MeetingStore.addOrRemovePendingRequests( pendingRequests )
            MeetingStore.setRaisedHands( handRaises )
            MeetingStore.setUsersPermissions( userPermissions )

            await this.syncMediaPermissions( localUser )
            this.meetingId = meetingId

            // 4. Virtual Board Sync
            if ( virtualBoardActive ) {
                const vbSync = await OnlineSessionSocketStore.emit( "virtualboard:sync", { room_id: meetingId } )
                if ( vbSync.success ) {
                    MeetingStore.setMeetingState( { presentingVirtualBoard: true } )
                    VirtualboardStore.setBoardState( {
                        currentSlide: vbSync.presentation_document.currentSlide,
                        slides: vbSync.presentation_document.slides,
                        document: vbSync.presentation_document,
                    } )
                    VirtualboardStore.setElements( vbSync.elements || {} )
                    MeetingStore.setPresentingDocument( vbSync.presentation_document )
                }
            }
        } catch ( err ) {
            console.error( "Critical Failure during orchestrateJoin:", err )

            // --- FAILSAFE CLEANUP ---
            if ( agoraJoined ) {
                await mainClient.leave().catch( e => console.error( "Failsafe: Agora leave failed", e ) )
            }

            if ( socketJoined ) {
                await OnlineSessionSocketStore.emit( 'meeting:leave', { reason: 'join-failed' } ).catch( e => console.error( "Failsafe: Socket leave failed", e ) )
                // We don't necessarily disconnect the whole socket unless required, 
                // but we ensure the server knows we left the room.
            }
            if ( !noReturn )
                MeetingStore.setMeetingState( { isJoined: false } )
            throw err
        }
    }
}

export default MeetingCoordinator